import { useMemo, CSSProperties } from 'react'
import { round } from 'shared/util'

import { CancerResult, CancerDiseaseResult } from 'interface/cancer'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import { CancerResultViewType } from './types'

import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components'

interface CancerResultSeries {
  title: string
  stack?: string
  barWidth?: number | string
  color?: string
  val: (record: CancerResult) => number | null
}
interface CancerResultSeriesMap {
  [index: string]: CancerResultSeries
}

const stackingBarWidth = '3%'

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
])

type CancerResultGraphProps = {
  list: CancerResult[]
  disease: CancerDiseaseResult
  viewType: number
  width?: number | 'auto'
  height?: number | 'auto'
  style?: CSSProperties
}
export const CancerResultGraph = ({
  list,
  disease,
  viewType,
  width,
  height,
  style
}: CancerResultGraphProps) => {
  const serieses = useMemo<CancerResultSeriesMap>(
    () => viewType === CancerResultViewType.Stage ? ({
      incubation: { val: (record) => record.stages?.incubation, color: '#aaaaaa', title: '潜伏期' },
      stage0: { val: (record) => record.stages?.stage0, color: '#339ca8', title: '０期' },
      stage0a: { val: (record) => record.stages?.stage0a, color: '#30919c', stack: 'stage0', barWidth: stackingBarWidth, title: '０a期' },
      stage0is: { val: (record) => record.stages?.stage0is, color: '#2b838c', stack: 'stage0', barWidth: stackingBarWidth, title: '０is期' },
      stage1: { val: (record) => record.stages?.stage1, color: '#005eaa', title: 'Ⅰ期' },
      stage1a: { val: (record) => record.stages?.stage1a, color: '#005aa3', stack: 'stage1', barWidth: stackingBarWidth, title: 'ⅠＡ期' },
      stage1b: { val: (record) => record.stages?.stage1b, color: '#004f8f', stack: 'stage1', barWidth: stackingBarWidth, title: 'ⅠＢ期' },
      stage1c: { val: (record) => record.stages?.stage1c, color: '#00437a', stack: 'stage1', barWidth: stackingBarWidth, title: 'ⅠＣ期' },
      stage2: { val: (record) => record.stages?.stage2, color: '#2b821d', title: 'Ⅱ期' },
      stage2a: { val: (record) => record.stages?.stage2a, color: '#26751a', stack: 'stage2', barWidth: stackingBarWidth, title: 'ⅡＡ期' },
      stage2b: { val: (record) => record.stages?.stage2b, color: '#206416', stack: 'stage2', barWidth: stackingBarWidth, title: 'ⅡＢ期' },
      stage2c: { val: (record) => record.stages?.stage2c, color: '#1b5412', stack: 'stage2', barWidth: stackingBarWidth, title: 'ⅡＣ期' },
      stage3: { val: (record) => record.stages?.stage3, color: '#e6b600', title: 'Ⅲ期' },
      stage3a: { val: (record) => record.stages?.stage3a, color: '#e0b000', stack: 'stage3', barWidth: stackingBarWidth, title: 'ⅢＡ期' },
      stage3b: { val: (record) => record.stages?.stage3b, color: '#cca000', stack: 'stage3', barWidth: stackingBarWidth, title: 'ⅢＢ期' },
      stage3c: { val: (record) => record.stages?.stage3c, color: '#b89000', stack: 'stage3', barWidth: stackingBarWidth, title: 'ⅢＣ期' },
      stage4: { val: (record) => record.stages?.stage4, color: '#c12e34', title: 'Ⅳ期' },
      stage4a: { val: (record) => record.stages?.stage4a, color: '#b62b2f', stack: 'stage4', barWidth: stackingBarWidth, title: 'ⅣＡ期' },
      stage4b: { val: (record) => record.stages?.stage4b, color: '#a5272b', stack: 'stage4', barWidth: stackingBarWidth, title: 'ⅣＢ期' },
      stage4c: { val: (record) => record.stages?.stage4c, color: '#952327', stack: 'stage4', barWidth: stackingBarWidth, title: 'ⅣＣ期' },
      prestage: { val: (record) => record.stages?.prestage, color: '#32a487', title: '施術前' },
      prediction: {
        val: (record) => round(
          ((record.stages?.stage0 ?? 0) * disease.factor.stage0) / 100 +
          ((record.stages?.stage1 ?? 0) * disease.factor.stage1) / 100 +
          ((record.stages?.stage2 ?? 0) * disease.factor.stage2) / 100 +
          ((record.stages?.stage3 ?? 0) * disease.factor.stage3) / 100 +
          ((record.stages?.stage4 ?? 0) * disease.factor.stage4) / 100,
          1
        ),
        color: '#ed6c02',
        title: '再発F値'
      },
    } as CancerResultSeriesMap) : viewType === CancerResultViewType.AgeAndGenders ? ({
      age0to14: { val: (record) => record.ages?.age0to14, color: '#91cc75', stack: 'age', title: '15歳未満' },
      age15to39: { val: (record) => record.ages?.age15to39, color: '#32a487', stack: 'age', title: '15～39歳' },
      age40to64: { val: (record) => record.ages?.age40to64, color: '#2b821d', stack: 'age', title: '40～64歳' },
      age65to74: { val: (record) => record.ages?.age65to74, color: '#e6b600', stack: 'age', title: '65～74歳' },
      age75to84: { val: (record) => record.ages?.age75to84, color: '#ed6c02', stack: 'age', title: '75～84歳' },
      age85over: { val: (record) => record.ages?.age85over, color: '#aaaaaa', stack: 'age', title: '85歳以上' },
      male: { val: (record) => record.gender?.male, color: '#005eaa', stack: 'gender', title: '男性' },
      female: { val: (record) => record.gender?.female, color: '#c12e34', stack: 'gender', title: '女性' },
    } as CancerResultSeriesMap) : ({
    } as CancerResultSeriesMap),
    [viewType, disease]
  )

  const reverseList = useMemo(() => [...list].reverse(), [list])

  const validSerieses = useMemo<CancerResultSeries[]>(
    () => {
      return Object.keys(serieses)
        .filter((columnName) => list.some((r) => serieses[columnName].val(r)))
        .map((columnName) => serieses[columnName])
    },
    [serieses, list]
  )
  if (validSerieses.length === 0) return <></>

  return (
    <ReactEChartsCore
      echarts={echarts}
      style={style}
      notMerge={true}
      option={{
        //animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 10,
          bottom: 10,
        },
        grid: {
          top: 10,
          left: 10,
          right: 120,
          bottom: 10,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: reverseList.map((record) => `${record.year}年`),
        },
        yAxis: {
          type: 'value',
        },
        series: validSerieses.map((series) => ({
          name: series.title,
          type: 'bar',
          stack: series.stack,
          barWidth: series.barWidth,
          label: { show: true },
          barGap: '5%',
          emphasis: { focus: 'series' },
          itemStyle: { color: series.color },
          data: reverseList.map((record) => series.val(record)),
        })),
        textStyle: {
          fontFamily: 'Noto Sans JP,sans-serif',
        },
      }}
      opts={{
        renderer: 'canvas',
        width: width,
        height: height,
      }}
      theme="insite"
    />
  )
}
